import { useForm } from "react-hook-form";
import MainContainer from "../../../components/container/MainContainer";
import { useNotification } from "../../../components/notification/NotificationContainer";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useLayoutEffect } from "react";
import {
  addProposalBasics,
  getProject,
  unsetSelectedProject,
} from "../../../features/projectsSlice";
import Loading from "../../../components/loading/Loading";
import PageTitle from "../../../components/titles/PageTitle";
import InputField from "../../../components/form/InputField";
import { formatDate, getDate, getTodayDate } from "../../../util";
import EditorField from "../../../components/form/EditorField";
import { zodResolver } from "@hookform/resolvers/zod";
import { addProposalBasicsSchema } from "../../../schemas";
import loadStatus from "../../../util/loadStatus";
import NetworkError from "../../error/NetworkError";

const ProposalBasic = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const project = useSelector((state) => state.projects.selected);
  const projectLoad = useSelector((state) => state.projects.load.getProject);
  const addProposalLoad = useSelector(
    (state) => state.projects.load.addProposalBasics
  );
  const addNotification = useNotification();
  const {
    register,
    reset,
    setValue,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: { description: "", title: "" },
    resolver: zodResolver(addProposalBasicsSchema),
  });

  const onSubmit = async (data, e) => {
    e.preventDefault();
    try {
      const res = await dispatch(addProposalBasics(data));
      if (addProposalBasics.fulfilled.match(res)) {
        addNotification("Successfully saved!", "success");
        return navigate("/proposals/background/" + id);
      } else {
        addNotification(res.payload.message, "error");
      }
    } catch (error) {
      addNotification(error.mesage, "warning");
    }
  };

  useLayoutEffect(() => {
    dispatch(getProject(id));

    return () => {
      dispatch(unsetSelectedProject());
    };
  }, []);

  useEffect(() => {
    if (project.id) {
      reset({
        title: project.name,
        description: project.description,
        planned_start_date: project.planned_start_date
          ? formatDate(project.planned_start_date)
          : getTodayDate(),
        planned_end_date: project.planned_end_date
          ? formatDate(project.planned_end_date)
          : getDate(90),
      });
    }
  }, [project]);

  if (
    (id && project.id && project.stage.step_number < 8) ||
    (id && project.id && project.stage.step_number > 8)
  ) {
    return navigate("/requests/my-requests");
  }

  if (projectLoad === loadStatus.loading) {
    return <Loading />;
  }

  if (projectLoad === loadStatus.error) {
    return <NetworkError to={location.pathname} />;
  }

  return (
    <MainContainer>
      <div className="w-full max-w-5xl mx-auto">
        <PageTitle title="Planning  - Proposal Basics" />
        {project.id && (
          <form
            onSubmit={handleSubmit(onSubmit)}
            method="post"
            encType="multipart/form-data"
          >
            <div className="text-text-1/70  border border-bkg2 rounded-md p2 flex flex-col gap-1.5 p-5 mb-4 font-roboto">
              <h5 className="text-text-1/80 ">Proposal Basics</h5>
            </div>
            <div className="text-text-1/60  border border-bkg2 rounded-md p2 flex flex-col gap-1.5 p-5 mb-4 bg-content">
              <InputField
                register={register}
                type="text"
                name="title"
                label="Title"
                defaultValue={project.name}
                errors={errors.title}
                autoComplete="off"
              />
              <EditorField
                register={register}
                setValue={setValue}
                label="Background"
                name="description"
                errors={errors.description}
                className="border-bkg2"
                defaultValue={project.description}
              />
              <div className="sm:flex justify-between items-center gap-7">
                <InputField
                  register={register}
                  name="planned_start_date"
                  type="date"
                  label="Planned Start Date"
                  errors={errors.planned_start_date}
                  defaultValue={getTodayDate()}
                />
                <div className="input-container sm:invisible"></div>
                <InputField
                  register={register}
                  name="planned_end_date"
                  type="date"
                  label="Planned End Date"
                  defaultValue={getDate(10)}
                  errors={errors.planned_end_date}
                />
              </div>
              <InputField
                register={register}
                name="id"
                type="hidden"
                defaultValue={id}
              />
              <div className="flex justify-between items-center pt-4 pb-10">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    navigate("/requests/phase-two/" + project.id);
                  }}
                  className="cancel-rss-btn"
                  disabled={addProposalLoad}
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={addProposalLoad}
                  className="add-rss-btn"
                >
                  Save & Continue
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </MainContainer>
  );
};

export default ProposalBasic;
